import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

const FIVE_MINUTES_MS = 5 * 60 * 1000
const ALL_DAY_MS = 24 * 60 * 60 * 1000

function normalizeTitle(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function getEventTimeRange(event) {
  const start = event?.start || {}
  const end = event?.end || {}

  let startMs = null
  let endMs = null

  if (start.dateTime) {
    const parsed = new Date(start.dateTime).getTime()
    if (!Number.isNaN(parsed)) startMs = parsed
  } else if (start.date) {
    const parsed = new Date(`${start.date}T00:00:00`).getTime()
    if (!Number.isNaN(parsed)) startMs = parsed
  }

  if (end.dateTime) {
    const parsed = new Date(end.dateTime).getTime()
    if (!Number.isNaN(parsed)) endMs = parsed
  } else if (end.date) {
    const parsed = new Date(`${end.date}T00:00:00`).getTime()
    if (!Number.isNaN(parsed)) endMs = parsed
  }

  if (startMs === null) return null

  if (endMs === null) {
    endMs = start.date ? startMs + ALL_DAY_MS : startMs
  }

  if (endMs < startMs) {
    endMs = start.date ? startMs + ALL_DAY_MS : startMs
  }

  return { startMs, endMs }
}

function eventsConflict(primaryEvent, secondaryEvent) {
  const primaryRange = getEventTimeRange(primaryEvent)
  const secondaryRange = getEventTimeRange(secondaryEvent)

  if (!primaryRange || !secondaryRange) {
    return false
  }

  const sameTitle =
    normalizeTitle(primaryEvent?.summary) !== '' &&
    normalizeTitle(primaryEvent?.summary) ===
      normalizeTitle(secondaryEvent?.summary)

  const startsClose =
    Math.abs(primaryRange.startMs - secondaryRange.startMs) <= FIVE_MINUTES_MS

  if (sameTitle && startsClose) {
    return true
  }

  return false
}

function compareEventsByStart(a, b) {
  const aRange = getEventTimeRange(a)
  const bRange = getEventTimeRange(b)
  const aStart = aRange ? aRange.startMs : Number.POSITIVE_INFINITY
  const bStart = bRange ? bRange.startMs : Number.POSITIVE_INFINITY

  if (aStart !== bStart) {
    return aStart - bStart
  }

  return String(a?.summary || '').localeCompare(String(b?.summary || ''))
}

function mergeSecondaryEvents(primaryEvents, secondaryEvents) {
  const acceptedSecondary = secondaryEvents.filter(
    (secondaryEvent) =>
      !primaryEvents.some((primaryEvent) =>
        eventsConflict(primaryEvent, secondaryEvent),
      ),
  )

  return [...primaryEvents, ...acceptedSecondary].sort(compareEventsByStart)
}

/**
 * Public Google Calendar events list (read-only)
 *
 * Required props:
 * - apiKey: Google API key (restricted to your site + Calendar API)
 * - calendarId: Google Calendar ID (e.g. xxxxx@group.calendar.google.com)
 *
 * Optional props:
 * - displayTimeZone: timezone to display times in
 * - maxResults: number of upcoming events to fetch
 * - locale: locale for date formatting
 * - secondaryEndpoint: optional endpoint for JSON events from PHP proxy
 */
export default function EventsCalendar({
  apiKey,
  calendarId,
  displayTimeZone = 'Pacific/Auckland',
  maxResults = 12,
  locale = 'en-NZ',
  secondaryEndpoint = '',
}) {
  const [events, setEvents] = useState([])
  const [status, setStatus] = useState('loading') // loading | success | empty | error
  const [errorMessage, setErrorMessage] = useState('')
  const [noticeMessage, setNoticeMessage] = useState('')
  const [expandedDetails, setExpandedDetails] = useState({})

  const endpoint = useMemo(() => {
    const params = new URLSearchParams({
      key: apiKey || '',
      singleEvents: 'true',
      orderBy: 'startTime',
      timeMin: new Date().toISOString(),
      maxResults: String(maxResults),
    })

    return `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
      calendarId || '',
    )}/events?${params.toString()}`
  }, [apiKey, calendarId, maxResults])

  const secondaryEndpointWithParams = useMemo(() => {
    if (!secondaryEndpoint) return ''

    const params = new URLSearchParams({
      timeMin: new Date().toISOString(),
      maxResults: String(maxResults),
    })

    const separator = secondaryEndpoint.includes('?') ? '&' : '?'
    return `${secondaryEndpoint}${separator}${params.toString()}`
  }, [secondaryEndpoint, maxResults])

  useEffect(() => {
    let isCancelled = false

    async function loadEvents() {
      if (!apiKey || !calendarId) {
        setStatus('error')
        setErrorMessage('Missing apiKey or calendarId.')
        return
      }

      setStatus('loading')
      setErrorMessage('')
      setNoticeMessage('')

      try {
        const googleRes = await fetch(endpoint)
        const googleData = await googleRes.json()

        if (!googleRes.ok) {
          const msg =
            googleData?.error?.message || `HTTP ${googleRes.status}`
          throw new Error(msg)
        }

        const googleItems = Array.isArray(googleData?.items)
          ? googleData.items
          : []
        let mergedItems = [...googleItems]

        if (secondaryEndpointWithParams) {
          try {
            const secondaryRes = await fetch(secondaryEndpointWithParams)
            const secondaryData = await secondaryRes.json()

            if (!secondaryRes.ok) {
              const secondaryMsg =
                secondaryData?.detail ||
                secondaryData?.error ||
                `HTTP ${secondaryRes.status}`
              throw new Error(secondaryMsg)
            }

            const secondaryItems = Array.isArray(secondaryData?.items)
              ? secondaryData.items
              : []

            mergedItems = mergeSecondaryEvents(googleItems, secondaryItems)
          } catch (secondaryErr) {
            const secondaryMsg =
              secondaryErr instanceof Error
                ? secondaryErr.message
                : 'Unknown secondary calendar error'
            setNoticeMessage(
              `Secondary calendar unavailable: ${secondaryMsg}. Showing Google events only.`,
            )
          }
        } else {
          mergedItems = [...googleItems].sort(compareEventsByStart)
        }

        if (isCancelled) return

        setEvents(mergedItems)
        setExpandedDetails({})

        if (mergedItems.length === 0) {
          setStatus('empty')
        } else {
          setStatus('success')
        }
      } catch (err) {
        if (isCancelled) return
        setStatus('error')
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    loadEvents()

    return () => {
      isCancelled = true
    }
  }, [endpoint, apiKey, calendarId, secondaryEndpointWithParams])

  function formatWhen(event) {
    const s = event?.start || {}
    const e = event?.end || {}

    // All-day event
    if (s.date) {
      const d = new Date(`${s.date}T00:00:00`)
      return `All day • ${d.toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: displayTimeZone,
      })}`
    }

    // Timed event
    if (s.dateTime) {
      const start = new Date(s.dateTime)
      const end = e.dateTime ? new Date(e.dateTime) : null

      const datePart = start.toLocaleDateString(locale, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: displayTimeZone,
      })

      const startPart = start.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: displayTimeZone,
      })

      const endPart = end
        ? end.toLocaleTimeString(locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: displayTimeZone,
          })
        : ''

      return endPart
        ? `${datePart} • ${startPart} – ${endPart}`
        : `${datePart} • ${startPart}`
    }

    return 'Date TBA'
  }

  function getFirstLine(text) {
    if (!text) return ''
    return text.split('\n').find((line) => line.trim().length > 0) || ''
  }

  function normalizeDescription(text) {
    if (!text) return ''

    return text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .trim()
  }

  function toggleDetails(eventId) {
    setExpandedDetails((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }))
  }

  return (
    <section style={styles.wrap}>
      <h2 style={styles.title}>Upcoming Events</h2>

      {status === 'loading' && <p style={styles.muted}>Loading events…</p>}

      {status === 'error' && (
        <p style={styles.error}>Could not load events: {errorMessage}</p>
      )}

      {status === 'empty' && (
        <p style={styles.muted}>
          There are no scheduled Taha Wairua Events. If you are interested in
          hosting or joining an event please get in touch via the{' '}
          <Link to="/contact" style={styles.inlineLink}>
            Contact
          </Link>{' '}
          page on the website.
        </p>
      )}

      {status === 'success' && (
        <>
          <p style={styles.muted}>
            Showing {events.length} upcoming event{events.length > 1 ? 's' : ''}
            .
          </p>
          {noticeMessage ? <p style={styles.notice}>{noticeMessage}</p> : null}

          <div style={styles.list}>
            {events.map((ev, index) => {
              const title = ev?.summary || 'Untitled event'
              const when = formatWhen(ev)
              const location = ev?.location || ''
              const description = normalizeDescription(ev?.description || '')
              const eventKey = `${ev?.id || `${title}-${when}`}-${index}`
              const isExpanded = Boolean(expandedDetails[eventKey])
              const previewDescription = getFirstLine(description)

              return (
                <article key={eventKey} style={styles.card}>
                  <h3 style={styles.cardTitle}>{title}</h3>
                  <p style={styles.meta}>🗓️ {when}</p>

                  {location ? <p style={styles.meta}>📍 {location}</p> : null}

                  {description ? (
                    <>
                      <p style={styles.description}>
                        {isExpanded ? description : previewDescription}
                      </p>
                      <div style={styles.detailsActionWrap}>
                        <button
                          type="button"
                          style={styles.detailsButton}
                          onClick={() => toggleDetails(eventKey)}
                        >
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      </div>
                    </>
                  ) : null}

                </article>
              )
            })}
          </div>
        </>
      )}
    </section>
  )
}

const styles = {
  wrap: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '16px',
  },
  title: {
    margin: '0 0 10px 0',
  },
  muted: {
    color: '#6b7280',
    marginTop: 0,
  },
  inlineLink: {
    color: '#0f6b07',
    fontWeight: 700,
    textDecoration: 'none',
  },
  notice: {
    color: '#4b5563',
    marginTop: 0,
  },
  error: {
    color: '#b00020',
    marginTop: 0,
  },
  list: {
    display: 'grid',
    gap: '12px',
  },
  card: {
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 12,
    padding: '12px 14px',
  },
  cardTitle: {
    margin: '0 0 8px 0',
  },
  meta: {
    margin: '6px 0',
  },
  description: {
    marginTop: 8,
    whiteSpace: 'pre-wrap',
  },
  detailsActionWrap: {
    marginTop: 8,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  detailsButton: {
    border: '1px solid #c9c9c9',
    background: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    cursor: 'pointer',
    font: 'inherit',
  },
}
